import { z } from 'zod';

/**
 * Verificacao em duas etapas (TOTP, RFC 6238).
 *
 * O algoritmo nao e da Microsoft nem de ninguem: o Authenticator da Microsoft,
 * o do Google e o 1Password leem o mesmo QR porque todos implementam o mesmo
 * RFC. Depois do cadastro nao ha troca de rede entre o app e este servidor —
 * os dois so calculam a mesma funcao do relogio sobre um segredo comum.
 *
 * Fica no pacote compartilhado o que os DOIS lados precisam concordar: o
 * formato do codigo, os nomes de cookie e quanto tempo a sessao dura.
 */

/** Digitos do codigo. Fixo em 6: e o que todo autenticador assume. */
export const TOTP_DIGITOS = 6;

/** Janela de cada codigo, em segundos. */
export const TOTP_PASSO_S = 30;

/**
 * Passos aceitos alem do atual, para cada lado.
 *
 * 1 significa aceitar o codigo anterior e o proximo — 90s de tolerancia no
 * total. Cobre o desencontro normal entre o relogio do celular e o do
 * servidor. Cada passo a mais triplica o espaco de acerto de quem chuta.
 */
export const TOTP_TOLERANCIA_PASSOS = 1;

/**
 * Sessao de quem tem duas etapas ligadas: 3 horas.
 *
 * Mais curta que as 12h do login so com senha, e nao mais longa. A segunda
 * etapa nao e um passe mais duradouro — e uma barreira a mais que se paga com
 * mais frequencia. Por isso "manter conectado" (7 dias) e IGNORADO para quem
 * tem 2FA: as duas coisas se contradizem, e vale a mais restritiva.
 */
export const TOTP_SESSAO_MAX_AGE_MS = 3 * 60 * 60 * 1000;

/**
 * Validade do desafio entre a senha e o codigo.
 *
 * Curto de proposito: e so a travessia de uma tela. Este cookie prova que a
 * senha passou e NAO da acesso a nada — sem ele, o segundo passo aceitaria um
 * codigo sem ninguem ter provado a senha.
 */
export const TOTP_DESAFIO_MAX_AGE_MS = 5 * 60 * 1000;

export const TOTP_DESAFIO_COOKIE = 'totp_desafio';

/** Nome que aparece no aplicativo autenticador. */
export const TOTP_EMISSOR = 'Apen Capital';

/**
 * O codigo digitado.
 *
 * Os apps mostram "123 456"; colar isso e o caminho natural, entao o espaco sai
 * aqui em vez de virar erro de validacao na cara de quem colou certo.
 */
export const codigoTotp = z
  .string()
  .transform((v) => v.replace(/\s+/g, ''))
  .pipe(
    z
      .string()
      .regex(new RegExp(`^\\d{${String(TOTP_DIGITOS)}}$`), 'O código tem 6 dígitos.'),
  );

/** Segundo passo do login. O desafio vai no cookie, nao no corpo. */
export const totpLoginRequest = z.object({ codigo: codigoTotp });
export type TotpLoginRequest = z.infer<typeof totpLoginRequest>;

/** Confirmacao do cadastro: so ativa depois que um codigo do app bate. */
export const totpAtivarRequest = z.object({ codigo: codigoTotp });

/**
 * Desligar exige a senha atual.
 *
 * Sem isso, uma aba esquecida aberta derruba a segunda etapa da conta. Pedir a
 * senha transforma "sessao sequestrada" em "sessao sequestrada e senha
 * conhecida", que e um degrau bem mais alto.
 */
export const totpDesativarRequest = z.object({
  senha: z.string().min(1, 'Informe sua senha.').max(200),
});

/** O que a tela de cadastro recebe para desenhar o QR. */
export interface TotpProvisionamento {
  /** `otpauth://totp/...` — o conteudo do QR, nada alem disso. */
  uri: string;
  /** O mesmo segredo em Base32, para quem nao consegue escanear. */
  segredo: string;
}

/** Resposta do primeiro passo do login quando a conta tem 2FA. */
export interface LoginPrecisaTotp {
  precisa_totp: true;
}
